import { useState, useEffect } from 'react';
import { apiFetch } from '../api/client';
import { Activity, TrendingDown, TrendingUp, Minus } from 'lucide-react';

interface AnomalyIndicator {
  id: number;
  machine_id: string;
  machine_name: string;
  area_name: string;
  company_name: string;
  db_name: string;
  axis_id: number;
  point_name: string;
  axis_name: string;
  axis_label: string;
  peak_as: number;
  peak_as_label: string;
  worst_as: number;
  worst_as_label: string;
  latest_as: number | null;
  latest_as_label: string | null;
  as_direction: string | null;
  days_active: number;
  detected_at: string;
}

const AS_COLOURS: Record<string, string> = {
  Healthy:     '#16a34a',
  Monitor:     '#0891b2',
  Investigate: '#d97706',
  Action:      '#ea580c',
  Urgent:      '#dc2626',
  'No data':   '#94a3b8',
};

function asColour(label: string | null) {
  return AS_COLOURS[label ?? 'No data'] ?? '#94a3b8';
}

function ScoreBadge({ score, label }: { score: number | null; label: string | null }) {
  if (score == null) return <span className="text-gray-400 text-xs">—</span>;
  const colour = asColour(label);
  return (
    <span
      className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full text-white"
      style={{ backgroundColor: colour }}
    >
      {score} <span className="font-normal opacity-80">{label}</span>
    </span>
  );
}

function DirIcon({ direction }: { direction: string | null }) {
  if (direction === 'improving') return <TrendingUp className="w-3.5 h-3.5 text-green-600" />;
  if (direction === 'worsening' || direction === 'declining') return <TrendingDown className="w-3.5 h-3.5 text-red-500" />;
  return <Minus className="w-3.5 h-3.5 text-gray-400" />;
}

interface Props {
  onMachineClick: (machineId: string, machineName: string, points: any[]) => void;
}

export function AnomalyIndicatorsPage({ onMachineClick }: Props) {
  const [indicators, setIndicators] = useState<AnomalyIndicator[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'active' | 'all'>('active');
  const [sortKey, setSortKey] = useState<'peak_as' | 'days_active' | 'machine_name'>('peak_as');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    setLoading(true);
    apiFetch<AnomalyIndicator[]>('/alarms/anomaly-indicators')
      .then(data => setIndicators(data || []))
      .catch(() => setIndicators([]))
      .finally(() => setLoading(false));
  }, []);

  const handleSort = (key: typeof sortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'peak_as' ? 'asc' : 'desc');
    }
  };

  const filtered = indicators
    .filter(() => true) // all indicators are active (no resolved_at in response)
    .sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'peak_as') cmp = (a.peak_as ?? 100) - (b.peak_as ?? 100);
      else if (sortKey === 'days_active') cmp = (b.days_active ?? 0) - (a.days_active ?? 0);
      else cmp = (a.machine_name ?? '').localeCompare(b.machine_name ?? '');
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const SortHeader = ({ col, label }: { col: typeof sortKey; label: string }) => (
    <th
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer select-none hover:text-gray-700"
      onClick={() => handleSort(col)}
    >
      <span className="flex items-center gap-1">
        {label}
        {sortKey === col && (
          <span className="text-[#0891b2]">{sortDir === 'asc' ? '↑' : '↓'}</span>
        )}
      </span>
    </th>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#0891b2]/10 flex items-center justify-center">
            <Activity className="w-5 h-5 text-[#0891b2]" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Anomaly Indicators</h1>
            <p className="text-sm text-gray-500">Axes with elevated spectral activity (score &lt; 70)</p>
          </div>
        </div>
        <div className="text-sm text-gray-500">
          {loading ? '...' : `${filtered.length} active`}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="h-64 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-[#0891b2] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="h-64 flex flex-col items-center justify-center text-gray-400 gap-2">
            <Activity className="w-8 h-8 opacity-40" />
            <p className="text-sm">No anomaly indicators active.</p>
            <p className="text-xs">All monitored axes are within normal spectral range.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <SortHeader col="machine_name" label="Machine / Point" />
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Area</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Axis</th>
                  <SortHeader col="peak_as" label="Peak Score" />
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Current</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Trend</th>
                  <SortHeader col="days_active" label="Days Active" />
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Detected</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(ind => (
                  <tr
                    key={ind.id}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => onMachineClick(ind.machine_id, ind.machine_name, [])}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{ind.machine_name}</div>
                      <div className="text-xs text-gray-500">{ind.point_name}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      <div>{ind.area_name}</div>
                      <div className="text-gray-400">{ind.company_name}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-medium text-gray-700 bg-gray-100 px-2 py-0.5 rounded">
                        {ind.axis_label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <ScoreBadge score={ind.peak_as} label={ind.peak_as_label} />
                    </td>
                    <td className="px-4 py-3">
                      <ScoreBadge score={ind.latest_as} label={ind.latest_as_label} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <DirIcon direction={ind.as_direction} />
                        <span className="text-xs text-gray-500 capitalize">{ind.as_direction ?? '—'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      {ind.days_active != null ? `${ind.days_active}d` : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {ind.detected_at ? ind.detected_at.slice(0, 10) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
